import React from "react";
import { EntityEditProvider } from "@/contexts/EntityEditContext";
import type { EntityEditProviderProps } from "@/types/contexts/entityEdit";
import { ClientType } from "@/types/db/clients";
import { clientService } from "@/services/clientService";
import { FromDB } from "@/types";

interface ClientEditProviderProps extends Omit<EntityEditProviderProps<ClientType>, "onSave" | "entityName"> {
  children: React.ReactNode;
}

export function ClientEditProvider({ children, entityData, loading, onSaveSuccess }: ClientEditProviderProps) {
  const handleSave = async (
    formData: Partial<FromDB<ClientType>>,
    metadata?: Record<string, any>,
  ): Promise<ClientType> => {
    if (!entityData?.id) {
      throw new Error("No client ID available");
    }

    // Extract relational data that needs to be handled separately
    const {
      id,
      created_at,
      updated_at,
      client_abilities,
      client_restrictions,
      client_banned_users,
      client_preferred_users,
      client_locations,
      client_contacts,
      ...cleanFormData
    } = formData;

    // Handle logo upload if present in metadata
    if (metadata?.logoFile) {
      await clientService.uploadLogo(entityData.id, metadata.logoFile);
    } else if (metadata?.logoFile === null) {
      // User explicitly removed logo
      await clientService.deleteLogo(entityData.id);
    }

    // Update the base clients table (only direct columns)
    const updatedClient = await clientService.updateClient(entityData.id, cleanFormData);

    // Sync link table data if modified
    if (client_abilities !== undefined) {
      await clientService.syncClientAbilities(entityData.id, client_abilities || []);
    }

    if (client_restrictions !== undefined) {
      await clientService.syncClientRestrictions(entityData.id, client_restrictions || []);
    }

    if (client_banned_users !== undefined) {
      await clientService.syncBannedUsers(entityData.id, client_banned_users || []);
    }

    if (client_preferred_users !== undefined) {
      await clientService.syncPreferredUsers(entityData.id, client_preferred_users || []);
    }

    if (client_locations !== undefined) {
      await clientService.syncClientLocations(entityData.id, client_locations || []);
    }

    if (client_contacts !== undefined) {
      await clientService.syncClientContacts(entityData.id, client_contacts || []);
    }

    // Fetch the complete client data with all joins to return
    const completeClient = await clientService.getClientById(entityData.id);
    if (!completeClient) {
      throw new Error("Failed to fetch updated client data");
    }

    return completeClient;
  };

  return (
    <EntityEditProvider
      entityData={entityData}
      loading={loading}
      onSave={handleSave}
      onSaveSuccess={onSaveSuccess}
      entityName="Client"
    >
      {children}
    </EntityEditProvider>
  );
}
